/*
 * Copyright (c) 2022 E-WIND - Thais Watanabe.
 * All rights reserved.
 */
import React, { useState, useEffect, Fragment, useRef } from "react";
import { API, Auth, graphqlOperation } from "aws-amplify";
import { createMaker, updateMaker } from "src/graphql/mutations";
import { Dialog, Transition } from "@headlessui/react";
import toast from "react-hot-toast";
import { XMarkIcon } from "@heroicons/react/24/outline";

const initialState = { name: "" };

/**
 * The AddMakerModal component
 *
 * @author Thais Watanabe
 */
const AddMakerModal = (props) => {
  const {
    setIsOpen,
    isOpen,
    makers,
    setMakers,
    selectedMaker,
    setSelectedMaker,
  } = props;
  const [formState, setFormState] = useState(initialState);
  const [loggedUser, setLoggedUser] = useState("");
  const [error, setError] = useState(false);

  const cancelButtonRef = useRef(null);

  /* useEffect */
  useEffect(() => {
    if (selectedMaker) {
      const selected = {
        name: selectedMaker.name,
      };
      setFormState(selected);
    }
  }, [selectedMaker]);

  /* useEffect */
  useEffect(() => {
    Auth.currentUserInfo()
      .then((result) => {
        setLoggedUser(result.username);
      })
      .catch((error) => console.log(error));
  }, []);

  /**
   * setInput
   * @param {*} key
   * @param {*} value
   */
  function setInput(key, value) {
    setFormState({ ...formState, [key]: value });
  }

  /**
   * submit
   */
  async function submit() {
    var error = false;

    if (!formState.name) {
      setError(true);
      error = true;
    } else {
      setError(false);
      error = false;
    }

    if (!error) {
      if (selectedMaker) {
        update();
      } else {
        create();
      }
    }
  }

  /**
   * update
   */
  async function update() {
    try {
      selectedMaker.name = formState.name;
      delete selectedMaker._deleted;
      delete selectedMaker._lastChangedAt;

      setIsOpen(!isOpen);
      setFormState(initialState);
      setSelectedMaker(null);

      await API.graphql(
        graphqlOperation(updateMaker, { input: selectedMaker })
      );
      toast.success("保存しました");
    } catch (err) {
      console.log(err);
      toast.error("エラーが発生しました");
    }
  }

  /**
   * addMaker
   */
  async function create() {
    try {
      const inputMaker = {
        ...formState,
        author: loggedUser,
      };

      setFormState(initialState);
      setIsOpen(!isOpen);

      const result = await API.graphql(
        graphqlOperation(createMaker, { input: inputMaker })
      );

      const newMaker = result.data.createMaker;
      setMakers([...makers, newMaker]);
      toast.success("保存しました");
    } catch (err) {
      console.log(err);
      toast.error("エラーが発生しました");
    }
  }

  /**
   * closeModal
   */
  function closeModal() {
    setIsOpen(!isOpen);
    setFormState(initialState);
    setSelectedMaker(null);
  }

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed z-10 inset-0 overflow-y-auto"
        initialFocus={cancelButtonRef}
        onClose={closeModal}
      >
        <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          {/* This element is to trick the browser into centering the modal contents. */}
          <span
            className="hidden sm:inline-block sm:align-middle sm:h-screen"
            aria-hidden="true"
          >
            &#8203;
          </span>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <div
              className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6"
              role="dialog"
              aria-modal="true"
              aria-labelledby="modal-headline"
            >
              <div className="hidden sm:block absolute top-0 right-0 pt-4 pr-4">
                <button
                  type="button"
                  className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500"
                  onClick={() => closeModal()}
                >
                  <span className="sr-only">Close</span>
                  {/* <!-- Heroicon name: x --> */}
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <form>
                <div>
                  <div>
                    <h2 className="text-lg leading-6 font-medium text-gray-900">
                      {selectedMaker ? "メーカー修正" : "新規メーカー登録"}
                    </h2>
                  </div>

                  <div className="mt-6 gap-y-6 gap-x-4 sm:grid-cols-6">
                    <div className="py-2 sm:col-span-2">
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700"
                      >
                        名前
                      </label>
                      <input
                        value={formState.name}
                        onChange={(event) =>
                          setInput("name", event.target.value)
                        }
                        type="text"
                        name="name"
                        id="name"
                        autoComplete="cc-given-name"
                        className={`${
                          error && formState.name.length < 1
                            ? "pr-10 border-red-300 text-red-900 placeholder-red-300 focus:outline-none focus:ring-red-500 focus:border-red-50"
                            : "focus:ring-teal-500 focus:border-teal-500 border-gray-300"
                        } block w-full shadow-sm sm:text-sm rounded-md`}
                      />
                    </div>
                    {error && formState.name.length < 1 && (
                      <p className="mt-2 text-sm text-red-600">
                        「名前」を入力してください
                      </p>
                    )}
                  </div>
                </div>

                <div className="mt-5 sm:mt-6 sm:grid sm:grid-cols-2 sm:gap-3 sm:grid-flow-row-dense">
                  <button
                    onClick={() => submit()}
                    type="button"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-teal-600 text-base font-medium text-white hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:col-start-2 sm:text-sm"
                  >
                    保存する
                  </button>
                  <button
                    type="button"
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 sm:mt-0 sm:col-start-1 sm:text-sm"
                    onClick={() => closeModal()}
                  >
                    キャンセル
                  </button>
                </div>
              </form>
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default AddMakerModal;
